import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { readStates, states } from '@/constants/lifecycle';
import findResult, { findMatch } from '@/utils/resultsHelper';
import { isValidDataModel, isVisibleDataModel } from '../../datamodels/helper';
import { canEditDataset } from '../../helper';
import {
  orderedDictionaries, selectedDictionaryCode, validationInProgress, validationServerErrors,
} from '../helper';

const useDataManagementDataModelItem = (category, code) => {
  const withItem = (validationDataset, dataset) => ({
    disableValidate: validationInProgress(validationDataset),
    isLoading: validationDataset.readStatus === states.PENDING,
    lastValidationDate: validationDataset.data_model?.last_validation_at || dataset.data_model.last_validation_at,
    name: dataset.catalogue.title,
    valid: isValidDataModel(validationDataset.data_model?.last_validation_status
      || dataset.data_model.last_validation_status),
  }
  );

  const withoutItem = (dataset) => ({
    lastValidationDate: dataset.data_model.last_validation_at,
    name: dataset.catalogue.title,
    valid: isValidDataModel(dataset.data_model.last_validation_status),
  });

  return useSelector(({
    datasets: { datasets },
    datasetDataManagement: { dataManagement },
  }) => {
    const dataset = findResult(datasets, 'dataManagement', code);
    const item = findResult(dataManagement, category, code);

    return item ? withItem(item, dataset) : withoutItem(dataset);
  });
};

const useDataManagementDictionaryItem = (category, code, dictionaryCode) => {
  const withItem = (validationDataset, dataset) => {
    const selectedDictCode = selectedDictionaryCode(dataset, validationDataset);
    const validationDictionary = findMatch(validationDataset.dictionaries, dictionaryCode);
    const datasetDictionary = findMatch(dataset.dictionaries, dictionaryCode);

    const unmanagedData = dataset.status.managed_data.supported === false;
    const dictionaryStatus = validationDictionary?.status || datasetDictionary.status;

    return {
      allowConversion: !unmanagedData,
      dataTableExists: !unmanagedData && dictionaryStatus.data_exists,
      dictionaryName: validationDictionary?.name || datasetDictionary.name,
      disableConvert: unmanagedData || !dictionaryStatus.data_exists,
      disableValidate: unmanagedData || validationInProgress(validationDataset),
      isLoading: validationDataset.readStatus === states.PENDING || validationDictionary?.readStatus === states.PENDING,
      lastValidationDate: dictionaryStatus.last_validation_at,
      rowCount: dictionaryStatus.row_count,
      selected: !unmanagedData && selectedDictCode === dictionaryCode,
      unmanagedData,
      valid: validationDictionary?.valid,
    };
  };

  const withoutItem = (dataset) => {
    const selectedDictCode = selectedDictionaryCode(dataset);
    const dictionary = findMatch(dataset.dictionaries, dictionaryCode);

    const unmanagedData = dataset.status.managed_data.supported === false;

    return {
      allowConversion: !unmanagedData,
      dataTableExists: dictionary.status.data_exists,
      dictionaryName: dictionary.name,
      disableConvert: unmanagedData || !dictionary.status.data_exists,
      disableValidate: unmanagedData,
      lastValidationDate: dictionary.status.last_validation_at,
      rowCount: dictionary.status.row_count,
      selected: !unmanagedData && selectedDictCode === dictionary.code,
      unmanagedData,
      valid: null,
    };
  };

  return useSelector(({
    datasets: { datasets },
    datasetDataManagement: { dataManagement },
  }) => {
    const dataset = findResult(datasets, 'dataManagement', code);
    const item = findResult(dataManagement, category, code);

    return item ? withItem(item, dataset, dictionaryCode) : withoutItem(dataset, dictionaryCode);
  });
};

const useDataManagementActions = (category, code) => useSelector(({
  auth: { role: { permissions } },
  datasets: { datasets },
  datasetDataManagement: { dataManagement },
}) => {
  const dataset = findResult(datasets, 'dataManagement', code);
  const validationDataset = findResult(dataManagement, category, code);
  const unmanagedData = dataset?.status?.managed_data?.supported === false;
  const hasDictionaries = !!dataset?.dictionaries?.length;
  const hasDictionaryData = !isEmpty(dataset?.dictionaries?.find((d) => d.status.data_exists === true));
  const isLoading = validationDataset?.readStatus === states.PENDING || !!(validationDataset?.dictionaries || [])
    .find((d) => d?.readStatus === states.PENDING);

  return {
    allowValidate: hasDictionaries && !unmanagedData && !isLoading && permissions.datasets.update,
    allowConversion: !unmanagedData,
    conversionEnabled: hasDictionaryData && !unmanagedData && !isLoading && permissions.datasets.update,
    datasetName: dataset?.catalogue.title,
  };
});

const useValidateDataResultsDialog = () => useSelector(({
  datasetDataManagement: {
    validateDataResultsDialog: {
      category, code, dictionaryCode, dictionaryName, valid, errors,
    },
  },
}) => ({
  category,
  code,
  dictionaryCode,
  dictionaryName,
  valid,
  errors,
}));

const useValidateDataResultsPanel = (category, code) => {
  const withItem = (validationDataset, dataset) => {
    const selectedDictCode = selectedDictionaryCode(dataset, validationDataset);
    const validationDictionary = findMatch(validationDataset.dictionaries, selectedDictCode);
    const datasetDictionary = findMatch(dataset.dictionaries, selectedDictCode);

    return {
      readStatus: validationDictionary?.readStatus || states.NONE,
      readErrors: validationDictionary?.readErrors || [],
      valid: validationDictionary?.valid,
      errors: validationDictionary?.errors,
      dictionaryCode: validationDictionary?.code || datasetDictionary.code,
      dictionaryName: validationDictionary?.name || datasetDictionary.name,
      isLoading: validationDataset.readStatus === states.PENDING || validationDictionary?.readStatus === states.PENDING,
      isValidating: validationInProgress(validationDataset),
    };
  };

  const withoutItem = (dataset) => {
    const selectedDictCode = selectedDictionaryCode(dataset);
    const dictionary = findMatch(dataset.dictionaries, selectedDictCode);

    return {
      readStatus: states.NONE,
      readErrors: [],
      valid: null,
      errors: [],
      dictionaryCode: dictionary.code,
      dictionaryName: dictionary.name,
      isLoading: false,
    };
  };

  return useSelector(({
    datasets: { datasets },
    datasetDataManagement: { dataManagement },
  }) => {
    const dataset = findResult(datasets, 'dataManagement', code);
    const item = findResult(dataManagement, category, code);
    return item ? withItem(item, dataset) : withoutItem(dataset);
  });
};

const useDataManagementPanelHeader = (code) => useSelector(({
  datasets: { datasets },
}) => {
  const item = findResult(datasets, 'dataManagement', code) || {
    catalogue: { title: '' },
    ...readStates,
  };

  return {
    name: item.catalogue.title,
    readStatus: item.readStatus,
  };
});

const useDataManagementPanel = (category, code) => {
  const withoutItem = () => ({
    ...readStates,
    canRead: false,
    dictionaries: [],
    unmanagedData: null,
    unmanagedDataReasons: [],
  });

  const withItem = (item, validatedItem, role) => ({
    exists: true,
    hasDataModel: isVisibleDataModel(item.data_model),
    readStatus: item.readStatus,
    readErrors: item.readErrors,
    canRead: canEditDataset(item, role),
    dictionaries: orderedDictionaries(item.dictionaries),
    unmanagedData: item.status.managed_data.supported === false,
    unmanagedDataReasons: item.status.managed_data.reasons || [],
    validationReadErrors: validationServerErrors(validatedItem),
  });

  return useSelector(({
    datasets: { datasets },
    datasetDataManagement: { dataManagement },
    auth: { role },
  }) => {
    const item = findResult(datasets, 'dataManagement', code);
    const validatedItem = item ? findResult(dataManagement, category, code) : null;
    return item ? withItem(item, validatedItem, role) : withoutItem();
  });
};

export default {
  useDataManagementDataModelItem,
  useDataManagementDictionaryItem,
  useDataManagementActions,
  useValidateDataResultsDialog,
  useValidateDataResultsPanel,
  useDataManagementPanelHeader,
  useDataManagementPanel,
};
